/**
 * Calendar days (an inspection date, a plan's planned date) cross the API as the day's
 * UTC midnight: 10 March is `2027-03-10T00:00:00Z` for every user in every time zone.
 * That is how the backend reads a day (its UTC date) and how it writes one.
 *
 * Inside the app a day is the browser's LOCAL midnight, because every date comparison and
 * label here uses local days (`toDateString()`, the date pipe, date pickers). These
 * functions convert at the boundary, so a day never moves when it is shown in a different
 * zone - which the raw instant did: `2027-03-10T00:00:00Z` is still 9 March in Brazil.
 */

/** Local midnight of the value's UTC day. `2027-03-10T00:00:00Z` -> 10 Mar 00:00 local. */
export function utcDayToLocal(value: Date | string): Date {
  const date = new Date(value);
  const day = new Date(0);
  // setFullYear, not the constructor: it maps years 0-99 to 1900-1999, and the
  // server's "no date" is 0001-01-01.
  day.setFullYear(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  day.setHours(0, 0, 0, 0);
  return day;
}

/** The value's local day as that day's UTC midnight, in ISO form. */
export function localDayToUtcIso(value: Date | string): string {
  const date = new Date(value);
  const day = new Date(0);
  day.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
  day.setUTCHours(0, 0, 0, 0);
  return day.toISOString();
}

/**
 * The day work recorded in 3D counts as executed: the date 3D is showing when it is
 * earlier than today (the user is recording field work done that day), otherwise today.
 * Local midnight, like every day in the app.
 */
export function executionDay(referenceDate: Date | string | null | undefined, now: Date = new Date()): Date {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const reference = referenceDate ? new Date(referenceDate) : null;
  if (!reference || isNaN(reference.getTime())) return today;
  const day = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  return day < today ? day : today;
}

/**
 * How that day reads beside a save: "Today", or "<day> (3D date)". A string, so a
 * template can bind it without a new object on every check.
 */
export function executedOnPhrase(
  referenceDate: Date | string | null | undefined,
  formatDay: (day: Date) => string,
  now: Date = new Date()
): string {
  const day = executionDay(referenceDate, now);
  return day < executionDay(null, now) ? `${formatDay(day)} (3D date)` : 'Today';
}

// A zone-less ISO date-time is parsed as LOCAL time, so the app's existing
// `new Date(row.Date)` reads this as local midnight of the right day.
function localMidnightIso(value: string): string {
  const day = utcDayToLocal(value);
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  const year = String(day.getFullYear()).padStart(4, '0');
  return `${year}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T00:00:00`;
}

// `CheckLists` holds the same rows in the automatic-points requests (bot insertion, the
// point wizards, Risk Shield) and the Risk Shield configuration.
const isChecklistKey = (key: string) => ['checklist', 'checklists'].includes(key.toLowerCase());
const isDateKey = (key: string) => key.toLowerCase() === 'date';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

/**
 * Received data: every checklist row's `Date` becomes local midnight of its UTC day.
 * Mutates in place (the data was just parsed and nobody else holds it) and is idempotent.
 */
export function checklistDatesFromApi<T>(data: T): T {
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!isPlainObject(node)) return;
    for (const [key, value] of Object.entries(node)) {
      if (isChecklistKey(key) && Array.isArray(value)) {
        for (const row of value) {
          if (!isPlainObject(row)) continue;
          for (const field of Object.keys(row)) {
            const date = row[field];
            // Only a zoned instant (what the API emits); a zone-less string was already
            // converted, and anything unparseable is left alone.
            if (isDateKey(field) && typeof date === 'string' && /[zZ]|[+-]\d\d:?\d\d$/.test(date)
              && !isNaN(new Date(date).getTime())) {
              row[field] = localMidnightIso(date);
            }
          }
        }
      } else {
        visit(value);
      }
    }
  };
  visit(data);
  return data;
}

/**
 * Sent data: a copy in which every checklist row's `Date` is its local day as UTC
 * midnight. Copies rather than mutates, because the app keeps using its own objects.
 */
export function checklistDatesToApi<T>(data: T): T {
  const copy = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(copy);
    if (!isPlainObject(node)) return node;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      if (isChecklistKey(key) && Array.isArray(value)) {
        result[key] = value.map(row => {
          if (!isPlainObject(row)) return row;
          const next: Record<string, unknown> = { ...row };
          for (const field of Object.keys(next)) {
            const date = next[field];
            if (isDateKey(field) && (date instanceof Date || typeof date === 'string') && !isNaN(new Date(date).getTime())) {
              next[field] = localDayToUtcIso(date);
            }
          }
          return next;
        });
      } else {
        result[key] = copy(value);
      }
    }
    return result;
  };
  return copy(data) as T;
}
